import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider } from 'react-admin';
import { Button, DeleteButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { UserActionsIconButton } from 'shared/mui/IconButtons/UserActionsIconButton/UserActionsIconButton';
import { LimitsShowChipFields, LimitsShowTable } from 'shared/widgets/LimitsShow';
import { Limit } from 'types/embedded/limit';
import { Permission } from 'types/embedded/permission';

type Props = {
  open: boolean;
  onEdit: (record: Limit) => void;
  onDelete: (record: Limit) => void;
  onClose: () => void;
  record?: Limit;
};

export const MerchantsShowLimitsShowDialog: React.FC<Props> = ({
  open,
  onClose,
  onEdit,
  onDelete,
  record,
}) => {
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();
  const translate = useTypedTranslate();

  return (
    <Dialog fullWidth onClose={onClose} open={open}>
      <RecordContextProvider value={record}>
        <CardHeader
          appendActions={[
            hasResourceAccess(Resources.UserActions) && (
              <UserActionsIconButton entityName="limit" key="user-action-link" />
            ),
            <CloseIconButton key="close-button" onClose={onClose} />,
          ]}
          title={translate('app.limits.show.title')}
          titleSize="smallBold"
        />

        <CardContent>
          <LimitsShowChipFields />
          <LimitsShowTable />
        </CardContent>

        <CardActionsButtons
          rightActionsSlot={[
            hasResourcePermissions(Resources.Limits, [Permission.Full, Permission.Delete]) && (
              <DeleteButton
                key="delete"
                onClick={() => {
                  onClose();
                  record && onDelete(record);
                }}
                variant="outlined"
              />
            ),
            hasResourcePermissions(Resources.Limits, [Permission.Full, Permission.Update]) && (
              <Button
                key="edit"
                label={translate('app.buttons.edit')}
                onClick={() => {
                  onClose();
                  record && onEdit(record);
                }}
                variant="contained"
              />
            ),
          ]}
        />
      </RecordContextProvider>
    </Dialog>
  );
};
